import clsx from 'clsx';
import { OutlineButton, PrimaryButton } from 'Components/Buttons';
import SocialLoginBar from 'Components/SocialLoginBar';
import { get } from 'lodash-es';
import { EyeIcon, EyeOffIcon } from 'lucide-preact';
import { ReactNode } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { emailRegex } from '@/helpers';
import MagicLinkModal from '@/routes/login/MagicLinkModal';
import { showMagicLinkModal } from '@/routes/login/MagicLinkModal/actions';
import PasswordResetModal from '@/routes/login/ResetModal';
import { showModal } from '@/routes/login/ResetModal/actions';
import style from '@/routes/login/style.scss';

import performLogin from './performLogin';

export interface LoginResult {
  user: Record<string, any>;
  /** False when the address has not been verified — the caller still owes a verification step. */
  isRealEmail: boolean;
}

interface LoginFormProps {
  /** Offline day ticket purchase token from the URL. */
  odtp?: string | null;
  /** Mixpanel `method` for the login event. */
  trackingMethod?: string;
  /** An error surfaced by the caller, e.g. a failed social login round trip. */
  initialError?: string;
  /** Rendered under the buttons — a link on the page, a step switch in the modal. */
  footer?: ReactNode;
  onLoggedIn: (result: LoginResult) => void;
}

/**
 * The email/password login form, shared by the `/login` page and the Speedy Flow's log-in step.
 *
 * It owns the password-reset and magic-link modals rather than leaving them to the caller, so both
 * entry points get "forgot password" and "login without password" without either having to
 * remember to mount them.
 *
 * Everything page-shaped — the automatic magic-link and bundle-user logins, the third-party-app
 * credential handoff, the post-logout cleanup — stays on the login route: none of it can happen
 * inside a modal, because all of it is triggered by query params on a full page load.
 */
const LoginForm = ({
  odtp = null,
  trackingMethod = 'login-page',
  initialError = '',
  footer,
  onLoggedIn,
}: LoginFormProps) => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [formError, setFormError] = useState(initialError);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * See `SignupForm` — same defect, same shape. The caller resolves a failed social login in an
   * effect, which runs after this form has already mounted and read the prop once.
   */
  useEffect(() => {
    if (initialError) {
      setFormError(initialError);
    }
  }, [initialError]);

  const login = async (userEmail: string, userPassword: string) => {
    try {
      setIsLoading(true);
      const result = await performLogin({
        email: userEmail,
        password: userPassword,
        odtp,
        trackingMethod,
        dispatch,
      });
      onLoggedIn(result);
    } catch (err: any) {
      setIsLoading(false);
      console.error('Error while user logging in', err);
      if (err.statusCode === 401) {
        setFormError(get(err, 'body.error'));
      } else if (err.statusCode === 400) {
        setFormError('Wrong credentials, please try again.');
      } else if (err.statusCode === 403 && err?.body?.userDisabledAt) {
        localStorage.setItem('userDisabledAt', err.body.userDisabledAt);
        setFormError(get(err, 'body.error'));
      } else {
        setFormError('Something went wrong. Please try again!');
      }
    }
  };

  const onSubmit = (evt: Event) => {
    evt.preventDefault();
    setEmailError('');
    setPasswordError('');
    setFormError('');

    const trimmedEmail = (email || '').trim();
    let hasEmailError = false;
    let hasPasswordError = false;

    if (!trimmedEmail) {
      hasEmailError = true;
      setEmailError('Email cannot be empty');
    }
    if (!password) {
      hasPasswordError = true;
      setPasswordError('Password cannot be empty');
    }
    if (!hasEmailError && !trimmedEmail.match(emailRegex)) {
      hasEmailError = true;
      setEmailError('Invalid email');
    }
    if (!hasPasswordError && password.length < 8) {
      hasPasswordError = true;
      setPasswordError('Invalid Password');
    }
    if (hasEmailError || hasPasswordError) {
      return;
    }
    login(trimmedEmail, password);
  };

  return (
    <>
      <SocialLoginBar />
      <div className="relative mx-5 my-6 flex items-center justify-center">
        <div className="absolute inset-x-0 top-1/2 h-px bg-[#dadada]" />
        <span className="relative block bg-white px-4 text-xs/6 text-zinc-500">OR</span>
      </div>

      <form onSubmit={onSubmit} className={style.form}>
        <div className={style.inputContainer}>
          <div className={style.inputWrapper}>
            <input
              type="email"
              name="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={({ currentTarget }) => setEmail(currentTarget.value)}
              className={clsx(emailError && style.errorBorder)}
            />
          </div>
          <div className={clsx(style.errorSection, emailError && style.active)}>{emailError}</div>
        </div>

        <div className={style.inputContainer}>
          <div className={style.inputWrapper}>
            <input
              type={showPassword ? 'text' : 'password'}
              name="current-password"
              autoComplete="current-password"
              placeholder="Password"
              onChange={({ currentTarget }) => setPassword(currentTarget.value)}
              className={clsx(passwordError && style.errorBorder)}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeIcon className="size-[1em]" /> : <EyeOffIcon className="size-[1em]" />}
            </button>
          </div>
          <div className="flex w-full justify-between">
            <div className={clsx(style.errorSection, passwordError && style.active)}>{passwordError}</div>
            <div className="shrink-0 pr-1">
              <button
                type="button"
                className="preflight preflight-button text-sm text-grey-2 underline"
                onClick={() => dispatch(showModal(true, email?.trim() ?? ''))}
              >
                Forgot password?
              </button>
            </div>
          </div>
        </div>

        {formError && (
          <div className="my-4 flex justify-center">
            <div className={clsx(style.errorSection, style.active, '!w-auto')}>{formError}</div>
          </div>
        )}

        <div className="mt-6 space-y-6">
          <PrimaryButton
            label="Login"
            type="submit"
            fill="horizontal"
            loading={isLoading}
            className="mx-auto w-full max-w-[416px]"
          />
          <OutlineButton
            className="mx-auto mt-6 w-full max-w-[416px]"
            label="Login without password"
            type="button"
            onClick={() => dispatch(showMagicLinkModal(true, email?.trim() ?? ''))}
            fill="horizontal"
            loading={isLoading}
          />
        </div>
        {footer}
      </form>

      <PasswordResetModal />
      <MagicLinkModal />
    </>
  );
};

export default LoginForm;
